import { Component, OnInit } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { GlobalAnalyticsMapperUtil, TealiumAnalyticsEvent, TealiumAnalyticsService, TealiumConstants } from '@ddt-web/analytics';
import { ApplicationPathConstants } from '@ddt-web/config';
import { InjectConfig, ModelsExperienceConfigKey, SharedConfigKey } from '@ddt-web/config-layer';
import { SharedApplicationConstants } from '@ddt-web/domain';
import { ApplicationStateStoreProxyService } from '@ddt-web/shared-stores';
import { StoreService } from '@ddt-web/store-service';
import { keys } from 'highcharts';
import * as cloneDeep from 'lodash/cloneDeep';
import * as flatten from 'lodash/flatten';
import * as uniq from 'lodash/uniq';
import * as values from 'lodash/values';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { first } from 'rxjs/operators';

import { ApplicationConstants, ModelsBrowseStoreConfig } from '../config';
import { ModelsLevelCategories } from '../models';
import { ModelDropdown } from '../models/compare-dropdown.interface';
import { ModelsBrowseDataService, ModelsBrowseLinkActionsService } from '../services';
import { FamiliesUtil } from '../utils';

@Component({
	templateUrl: './mini-compare-dialog.component.html',
	styleUrls: ['./mini-compare-dialog.component.scss']
})
export class MiniCompareDialogComponent implements OnInit {
	@InjectConfig(ModelsExperienceConfigKey.ENABLE_ADD_TO_COMPARE_MODEL) public readonly enableAddToCompareModel: boolean = false;
	@InjectConfig(SharedConfigKey.SHOULD_USE_DAS_PORTFOLIO_ID) public readonly shouldUseDasPortfolioId: boolean = true;

	public selectedFamilyIds$: Observable<string[]>; // observable of set of selected familyIds for data table
	public selectedModelsList: ModelDropdown[] = [];
	public familyIdsArray$: Observable<string[]>[]; // list of familyId observable corresponding to each selectedModel
	public modelIdsArray$: Observable<string[]>[]; // list of modelId observable
	public familyModelsPerCategory: { [categoryId: string]: { [familyId: string]: string[] } } = {};
	public showOptionalData: boolean = true; // set as true for now to show all the data points
	public familiesId$: Observable<string[]>; // total list of familyIds observable for families dropdown
	public applicationConstants: ApplicationConstants = ApplicationConstants;
	public routedFromHome: boolean;
	public tealiumConstants = TealiumConstants;
	public familiesData: ModelsLevelCategories;
	public readonly GHOST_ROWS = Array(10).fill(0);
	public readonly GHOST_COLUMNS = Array(3).fill(0);

	constructor(
		private modelsBrowseDataService: ModelsBrowseDataService,
		private modelsBrowseLinkActionsService: ModelsBrowseLinkActionsService,
		private contentService: ContentService,
		private tealiumAnalyticService: TealiumAnalyticsService,
		private applicationStateStoreProxyService: ApplicationStateStoreProxyService,
		private storeService: StoreService
	) { }

	public ngOnInit(): void {
		combineLatest(
			[
				this.storeService.selectAll(ModelsBrowseStoreConfig.families),
				this.applicationStateStoreProxyService.getMeSelectedModelsData$()
			])
			.pipe(first(([families, meState]) => !families.isLoading && !meState.isLoading))
			.subscribe(([families, meState]) => {
				this.familiesData = FamiliesUtil.generateCategoriesModelLevel(families);
				const familyModelsPerCategory = this.familiesData?.familyModelsPerCategory;
				const familyIds = values(familyModelsPerCategory).map(category => keys(category));
				const ids = uniq(flatten(familyIds));
				this.selectedModelsList = meState?.modalData?.selectedModels as any ?? [];
				this.routedFromHome = meState?.modalData?.routedFrom === ApplicationPathConstants.MODELS_EXPERIENCE_PATHS.HOME;
				this.familyIdsArray$ = this.selectedModelsList.map((item => of([item.familyId])));
				this.familiesId$ = of(ids.map(id => id.toString()));
				this.setModelIdsArray();
				this.fetchMiniCompareData();
				this.updateSelectedFamilyIds();
			});
	}

	public onFamilyChange(selectedModels: ModelDropdown[]): void {
		this.selectedModelsList = selectedModels.map(item => item);
		this.familyIdsArray$ = selectedModels.map(item => of([item.familyId]));
		// fetch overview, holdings and performance data on value change in dropdown
		this.fetchMiniCompareData();
	}

	public removeSelectedModel(index: number): void {
		const listOfSelectedModels = cloneDeep(this.selectedModelsList);
		listOfSelectedModels.splice(index, 1);
		this.selectedModelsList = listOfSelectedModels;
		this.onFamilyChange(this.selectedModelsList);
	}

	public updateModel(modelId: string, index: number) {
		this.selectedModelsList[index]['modelId'] = modelId;
		this.updateSelectedFamilyIds();
		this.setModelIdsArray();
	}

	private updateSelectedFamilyIds(): void {
		this.selectedFamilyIds$ = of(uniq(this.selectedModelsList.map(model => model.familyId)));
	}
	public fetchMiniCompareData(): void {
		this.selectedModelsList.forEach((model) => {
			forkJoin([
				this.modelsBrowseDataService.fetchFamilyHoldings(model.familyId),
				this.modelsBrowseDataService.fetchFamilyModelsAnalytics(model.familyId)
			]).pipe(first()).subscribe();
		});
	}

	private setModelIdsArray(): void {
		this.modelIdsArray$ = this.selectedModelsList.map((item => of([item.modelId])));
	}

	public onRedirectTo360(): void {
		const modelIds = (this.shouldUseDasPortfolioId) ? this.selectedModelsList.map(item => item.dasPortfolioId)
			: this.selectedModelsList.map(item => item.modelId);

		const eventName = TealiumConstants.MODELS_EXPERIENCE_EVENTS.MODELS_EXPERIENCE_APP_NAME + ':' + TealiumConstants.MODELS_EXPERIENCE_EVENTS.FIND_AND_COMPARE_PAGE + `:mini compare modal:cta:launch full analysis:${modelIds.join(',')}`;
		this.tealiumAnalyticService.trackEventTealium(new TealiumAnalyticsEvent(eventName, { toolName: TealiumConstants.TOOL_NAMES.MODELS_EXPERIENCE }));
		this.tealiumAnalyticService.triggerToolCompletion(modelIds.join(','), TealiumConstants.TOOL_NAMES.MODELS_EXPERIENCE);

		this.modelsBrowseLinkActionsService.redirectOnLinkAction(
			SharedApplicationConstants.PORTFOLIO_ANALYSER_PAGE, ApplicationPathConstants.PA_PATHS.OVERVIEW, modelIds.toString()
		);
	}

	public onRedirectToHowToInvest(): void {
		// Redirect to how to invest, keeping as dcr driven for now
		GlobalAnalyticsMapperUtil.setPageName(TealiumConstants.MODELS_EXPERIENCE_EVENTS.HOW_TO_INVEST);
		const howToInvestLink = this.contentService.get('HOW_TO_INVEST_URL', 'https://blkcwpp002.bfm.com:9499/financial-professionals/financial-professionals/tools/how-to-invest');
		window.open(howToInvestLink);
	}
}
